<template>
  <v-dialog
    v-model="dialogFlag"
    max-width="500"
  >
    <v-card>
      <v-card-title class="d-flex justify-space-between align-center">
        <span class="primary--text">
          Email Addresses
        </span>
        <v-btn
          rounded
          width="72"
          color="primary lighten-2"
          :disabled="loading || !saveFlag"
          @click="saveChanges"
        >
          Save
        </v-btn>
      </v-card-title>

      <v-card-text class="px-0">
        <section class="section">
          <v-row>
            <v-col
              v-for="(unit, key) in formattedData"
              :key="`emailField_${key}`"
              cols="12"
            >
              <div class="d-flex align-center">
                <v-text-field
                  v-model="unit.email"
                  :rules="rules.email"
                  :label="unit.key"
                  dense
                  readonly
                />
                <v-btn
                  v-if="!(unit.key).includes('Primary')"
                  icon
                  :disabled="loading"
                  @click="deleteEmail(unit.email)"
                >
                  <v-icon
                    small
                  >
                    close
                  </v-icon>
                </v-btn>
              </div>
              <v-checkbox
                v-model="unit.primary"
                class="mt-0"
                label="Select as primary email address"
                dense
                hide-details
                readonly
                @click="setPrimary(unit.email)"
              />
            </v-col>
          </v-row>
        </section>
        <v-divider />

        <section class="section">
          <div class="font-weight-bold primary--text">
            Add new email address
          </div>
          <v-form
            id="addForm"
            ref="addForm"
            :disabled="loading"
            @submit.prevent="addEmail()"
          >
            <div class="d-flex align-center">
              <v-text-field
                v-model="newEmail"
                label="Email address"
                :rules="apiRules.new_email || rules.email"
                :disabled="formattedData && formattedData.length === 3"
                @change="apiRuleClear('new_email')"
              />
              <v-btn
                class="ml-3"
                color="primary lighten-2"
                outlined
                width="72"
                rounded
                :disabled="!newEmail || !newEmail.length"
                @click="addEmail"
              >
                Add
              </v-btn>
            </div>
          </v-form>
        </section>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions } from 'vuex';
import apiRules from '@/mixins/apiRules';
import accountApi from '@/api/account/account';

export default {
  name: 'EmailEditor',

  mixins: [
    apiRules,
  ],

  props: {
    dialog: {
      type: Boolean,
      default: false,
    },

    emailData: {
      type: Object,
      default: null,
    },
  },

  data: () => ({
    loading: false,
    saveFlag: false,
    primary: null,
    newEmail: '',
    rules: {
      email: [
        (v) => {
          if (!v) { return true; }
          const regex = /\S+@\S+\.\S+/;
          return regex.test(v) || 'Email is not valid';
        },
      ],
    },
  }),

  computed: {
    dialogFlag: {
      get() { return this.dialog; },
      set(newValue) {
        this.$emit('update:dialog', newValue);
      },
    },

    formattedData() {
      const emails = [];

      if (this.emailData) {
        emails.push({
          key: 'Primary Email',
          email: this.emailData.primary_email,
          primary: this.emailData.primary_email === this.primary,
        });

        if (this.emailData.alternative_emails.length > 0) {
          this.emailData.alternative_emails.forEach((el, index) => {
            emails.push({
              key: `Email ${index + 1}`,
              email: el,
              primary: el === this.primary,
            });
          });
        }
      } else {
        emails.push({
          key: 'Primary Email',
          email: '',
          primary: true,
        });
      }

      return emails;
    },
  },

  watch: {
    dialog: {
      immediate: true,
      handler(newValue) {
        if (newValue) {
          this.saveFlag = false;
          this.primary = this.emailData && this.emailData.primary_email
            ? this.emailData.primary_email : '';
        }
      },
    },
  },

  methods: {
    ...mapActions('account', [
      'getCurrentUser',
    ]),

    async saveChanges() {
      const confirm = await this.$confirm('You must login with your new primary email address after the changes have been saved. Are you sure you want to continue?');
      if (confirm) {
        try {
          this.loading = true;
          const params = { primary_email: this.primary };
          const response = await accountApi.patchSecurityEmail(params);
          this.$toast.success('The changes successfully saved.');
          this.$emit('update:emailData', response);
          await this.getCurrentUser();
          this.saveFlag = false;
          this.dialogFlag = false;
        } catch (err) {
          console.log(err);
          this.$toast.error(err.msg);
        } finally {
          this.loading = false;
        }
      }
    },

    async setPrimary(email) {
      if (email !== this.primary) {
        // const confirm = await this.$confirm(`Are you sure you want to change primary email as
        // ${email}? the change will be saved when you click the save button.`);
        this.primary = email;
        this.saveFlag = (email !== this.emailData.primary_email);
      }
    },

    async addEmail() {
      if (this.$refs.addForm.validate()) {
        try {
          this.loading = true;
          const params = { new_email: this.newEmail };
          const response = await accountApi.putSecurityEmail(params);
          this.$toast.success('Email successfully added.');
          this.$emit('update:emailData', response);
          this.$refs.addForm.reset();
        } catch (err) {
          console.log(err);
          if (err.field) {
            this.apiRules = err.field;
          } else if (err.msg) {
            this.$toast.error(err.msg);
          }
        } finally {
          this.loading = false;
        }
      }
    },

    async deleteEmail(email) {
      const confirm = await this.$confirm(`Are you sure you want to delete ${email} email?`);
      if (confirm) {
        try {
          this.loading = true;
          await accountApi.deleteSecurityEmail(email);
          this.$toast.success(`The Email ${email} successfully deleted.`);
          const updatedEmails = { ...this.emailData };
          updatedEmails.alternative_emails = [...updatedEmails.alternative_emails]
            .filter((el) => el !== email);
          this.$emit('update:emailData', updatedEmails);
        } catch (err) {
          console.log(err);
          if (err.msg) { this.$toast.error(err.msg); }
        } finally {
          this.loading = false;
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
  .section {
    padding: 20px 24px;
  }
</style>
